import { useCallback, useEffect, useMemo, useState } from 'react'
import { useLocalStorage } from 'react-use'

import {
  CartLine,
  CartLineInput,
  CartLineUpdateInput,
  CustomAttributeInput,
  FigsCart,
} from '../../../__generated__/graphql/catalog/graphql'
import { cookieKeys } from '../../../app/_config/Cookies.config'
import { useCheckoutClient } from '../../../app/_providers/CheckoutClientProvider.client'
import { useCookies } from '../../../app/_providers/CookiesProvider.client'
import { useFeatureFlag } from '../../../app/_providers/ExperimentationProvider.client'
import { useLocalization } from '../../../app/_providers/LocalizationProvider.client'
import { QueryStatus } from '../../hooks/types'

import { LocalCart, localCartIdStorageKey, localCartStorageKey } from './useLocalCart'
import { augmentLocalCartWithCartStoreIds, identifyCartLinesForMerge } from './utils'

export interface CartStoreLineItem {
  cartLineId: string
  sku: string | null
  quantity: number
  attributes: Array<{ key: string; value: string }>
  discounts: Array<{
    amount: number // amount discounted
    currencyCode: string
    message?: string
  }>
  cost: {
    currencyCode: string
    amountPerQuantity: number // the amount of the merchandise line
    subtotalAmount: number // the cost of the merchandise line before line-level discounts
    totalAmount: number // total cost of the merchandise line
    compareAtAmountPerQuantity?: number // compare at amount of the merchandise line
  }
}

export interface CartStoreCart {
  id: string | null // allow for null in the event we haven't made a real cart yet
  lines: CartStoreLineItem[]
  totalQuantity: number
  cost: {
    amount: number
    currencyCode: string
  }
  discounts: Array<{
    amount: number
    currencyCode: string
    message?: string
  }>
  attributes: Array<{ key: string; value: string }>
}

// `localStorageCart` is a UUID, cart line id is a UUID with additional content,
// so match based on start and end of string (^$) to strictly match UUIDs.
const UUID_REGEX = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i

export function useCartStore(
  localStorageCart?: LocalCart,
  onRemoteCartLinesAdd?: (newLines: LocalCart) => void,
  clearLocalStorageCart?: () => void
) {
  // TODO: we need to skip some degree of persistence if we have a gifting cart
  // TODO: pull CheckoutClient logic into this file. We don't need to keep separating concerns.
  const checkoutClient = useCheckoutClient()
  const { currency } = useLocalization() // regional currency to use as a fallback
  const [cookies, setCookie] = useCookies([cookieKeys.cartId.key])
  const [status, setStatus] = useState<Exclude<QueryStatus, 'idle'>>('pending')
  const [shopifyCart, setShopifyCart] = useState<Omit<FigsCart, 'checkoutUrl'>>()
  const [shouldAugment, setShouldAugment] = useState<boolean>(false)

  // initial values just for the merge process
  const [initialLocalCartId] = useLocalStorage<string>(localCartIdStorageKey)
  const [initialLocalCartLines] = useLocalStorage<LocalCart>(localCartStorageKey)
  const [activeCartId, setActiveCartId] = useState<string>()

  const shouldUseCartStoreDecision = useFeatureFlag<
    'cart-service-disabled' | 'cart-service-enabled'
  >({
    key: 'cart-service-checkout-transistion',
    controlVariant: 'cart-service-disabled',
    ifAccessedPriorToDecisionInitialization: 'return-null-while-pending',
  })

  const cartStoreEnabled = shouldUseCartStoreDecision === 'cart-service-enabled'

  // ensure cookie value is updated if the "active" `cartId` changes
  useEffect(() => {
    if (activeCartId && activeCartId !== cookies[cookieKeys.cartId.key]) {
      setCookie(cookieKeys.cartId.key, activeCartId, cookieKeys.cartId.options)
    }
  }, [activeCartId, cookies, setCookie])

  const cart = useMemo<CartStoreCart>(() => {
    // convert `shopifyCart` to preferred cart structure.
    if (shopifyCart) {
      return {
        id: shopifyCart.id!,
        lines:
          shopifyCart.lines?.edges
            .map(e => e.node)
            .filter(n => n !== null)
            .map(l => ({
              cartLineId: l.id,
              sku: l.sku,
              quantity: l.quantity,
              attributes: l.attributes?.filter(a => a !== null) ?? [],
              discounts: l.discountAllocations.map(e => ({
                amount: e.amount,
                currencyCode: e.currencyCode,
                message: e.title ?? undefined,
              })),
              cost: {
                currencyCode: l.cost.totalAmount.currencyCode,
                totalAmount: l.cost.totalAmount.amount,
                amountPerQuantity: l.cost.amountPerQuantity.amount,
                subtotalAmount: l.cost.subtotalAmount.amount,
                compareAtAmountPerQuantity: l.cost.compareAtAmountPerQuantity?.amount,
              },
            })) ?? [],
        cost: shopifyCart.cost,
        discounts:
          shopifyCart.discountDetails
            ?.filter(e => e !== null)
            .map(e => ({
              amount: e.amount,
              currencyCode: e.currencyCode,
              message: e.title ?? undefined,
            })) ?? [],
        attributes: shopifyCart.attributes?.filter(a => a !== null) ?? [], // TODO: are these really needed for anything UI related? Assess and potentially remove
        totalQuantity: shopifyCart.totalQuantity,
      }
    }
    // fallback data if `shopifyCart` isn't present
    return {
      id: null,
      lines: [],
      cost: {
        amount: 0,
        currencyCode: currency,
      },
      discounts: [],
      attributes: [],
      totalQuantity: 0,
    }
  }, [currency, shopifyCart])

  /**
   * Lots of weird stuff here, but most notably:
   * 1. we only want to augment once, so we check the `shouldAugment` state
   * 2. we need a `localStorageCart` to augment
   * 3. we need a callback from `useLocalCart` to update the local storage cart
   * 4. we need existing cart lines to augment the local cart lines with
   * 5. if we were unable to augment any items, they should be removed from `localStorageCart`
   */
  useEffect(() => {
    if (shouldAugment && localStorageCart && onRemoteCartLinesAdd && cart.lines.length > 0) {
      const hasLocalStorageItemsToAugment = Object.keys(localStorageCart).some(
        key => key.match(UUID_REGEX) !== null
      )
      if (hasLocalStorageItemsToAugment) {
        const newCart = augmentLocalCartWithCartStoreIds(localStorageCart, cart.lines)
        Object.keys(newCart).forEach(key => {
          // if we still have legacy UUID keys instead of Shopify line id keys,
          // we should remove the items as we weren't able to merge them.
          if (key.match(UUID_REGEX) !== null) {
            delete newCart[key]
          }
        })
        onRemoteCartLinesAdd(newCart)
      }
      setShouldAugment(false)
    }
  }, [onRemoteCartLinesAdd, cart.lines, localStorageCart, shouldAugment])

  const buildCartContentMap = useCallback(
    async (
      existingCartId: string | undefined,
      localCartId: string | undefined,
      localCartLines: LocalCart | undefined
    ): Promise<{
      cartContentsMap: Map<string, Omit<CartLine, 'cost' | 'discountAllocations'>[]>
      existingCart: Omit<FigsCart, 'checkoutUrl'> | null
      localStorageHasLines: boolean
      shouldClearLocalStorageCart: boolean
    }> => {
      const cartContentsMap = new Map<string, Omit<CartLine, 'cost' | 'discountAllocations'>[]>()
      let shouldClearLocalStorageCart = false
      let localStorageHasLines = !!localCartLines && Object.keys(localCartLines).length > 0
      const existingCart = existingCartId ? await checkoutClient.fetchCart(existingCartId) : null

      if (existingCartId) {
        if (existingCart) {
          cartContentsMap.set(
            existingCart.id,
            existingCart.lines?.edges.map(e => e.node).filter(e => e !== null) ?? []
          )
        } else {
          // if we have an existingCartId, but it was missing cart data,
          // we can assume we have already merged local cart contents into the last
          // cart store cart that was created. We can therefore clear the local
          // storage cart and omit any local storage data from the merge process.
          localStorageHasLines = false
          shouldClearLocalStorageCart = true
        }
      }

      if (localCartId && localStorageHasLines) {
        // if we have local storage items, retrieve them
        cartContentsMap.set(
          localCartId,
          Object.values(localCartLines!).map(line => ({
            id: line.variantId,
            sku: line.sku,
            quantity: line.quantity,
            attributes: Object.entries(line.properties)
              .filter((e): e is [string, string] => typeof e[1] === 'string' && e[1] !== '')
              .map(([key, value]) => ({ key: key, value: value })),
          }))
        )
      }

      return {
        cartContentsMap,
        existingCart,
        localStorageHasLines,
        shouldClearLocalStorageCart,
      }
    },
    [checkoutClient]
  )

  const mergeWithExistingCart = useCallback(
    async (
      existingCart: Omit<FigsCart, 'checkoutUrl'>,
      localCartId: string | undefined,
      localStorageHasLines: boolean,
      cartContentsMap: Map<string, Omit<CartLine, 'cost' | 'discountAllocations'>[]>
    ): Promise<void> => {
      const itemsToMerge: Omit<CartLine, 'cost' | 'discountAllocations'>[] = []
      const targetCartItems = cartContentsMap.get(existingCart.id)
      let localStorageItemsEvaluated = false

      // if we have local storage cart contents, we need to merge them
      if (localCartId && localStorageHasLines) {
        localStorageItemsEvaluated = true
        itemsToMerge.push(
          ...identifyCartLinesForMerge(
            cartContentsMap.get(localCartId) ?? [],
            targetCartItems ?? []
          )
        )
      }

      if (itemsToMerge.length > 0) {
        // update the cart
        const response = await checkoutClient.addLines({
          cartId: existingCart.id,
          lines: itemsToMerge.map(item => ({
            attributes: item.attributes,
            merchandiseId: item.id,
            quantity: item.quantity,
          })),
        })
        if (response) {
          setShopifyCart(response)
          setShouldAugment(true)
        }
      } else {
        // nothing to merge, just use existing cart as-is
        setShopifyCart(existingCart)
        if (localStorageItemsEvaluated) setShouldAugment(true)
      }
    },
    [checkoutClient]
  )

  const createNewCartFromLocalStorage = useCallback(
    async (
      localCartId: string,
      cartContentsMap: Map<string, Omit<CartLine, 'cost' | 'discountAllocations'>[]>
    ): Promise<string | undefined> => {
      const lines: CartLineInput[] = (cartContentsMap.get(localCartId) ?? []).map(e => ({
        merchandiseId: e.id,
        quantity: e.quantity,
        attributes: e.attributes,
      }))
      const response = await checkoutClient.createCart({
        lines,
      })
      if (response) {
        setShopifyCart(response)
        setShouldAugment(true)
        return response.id
      }
      return undefined
    },
    [checkoutClient]
  )

  const createNewCart = useCallback(async (): Promise<string | undefined> => {
    const response = await checkoutClient.createCart({ lines: [] })
    if (response) {
      setShopifyCart(response)
      return response.id
    }
    return undefined
  }, [checkoutClient])

  const fetchAndMergeCarts = useCallback(async () => {
    setStatus('pending')
    try {
      let outputCartId: string | undefined = undefined
      const cookieCartId = cookies[cookieKeys.cartId.key]
      const { cartContentsMap, existingCart, localStorageHasLines, shouldClearLocalStorageCart } =
        await buildCartContentMap(cookieCartId, initialLocalCartId, initialLocalCartLines)

      // if we found an existing Shopify cart, make it the active cart
      // and determine if we need to merge contents from local storage.
      if (existingCart && existingCart.id.length > 0) {
        await mergeWithExistingCart(
          existingCart,
          initialLocalCartId,
          localStorageHasLines,
          cartContentsMap
        )
        outputCartId = existingCart.id
      } else if (initialLocalCartId && localStorageHasLines) {
        outputCartId = await createNewCartFromLocalStorage(initialLocalCartId, cartContentsMap)
      } else {
        outputCartId = await createNewCart()
      }
      if (outputCartId) setActiveCartId(outputCartId)
      if (shouldClearLocalStorageCart) clearLocalStorageCart?.()
      setStatus('resolved')
    } catch {
      // anything we are `await`ing has a chance to fail.
      // if that happens, we should reject so we aren't stuck `pending`
      setStatus('rejected')
    }
  }, [
    buildCartContentMap,
    clearLocalStorageCart,
    cookies,
    createNewCart,
    createNewCartFromLocalStorage,
    initialLocalCartId,
    initialLocalCartLines,
    mergeWithExistingCart,
  ])

  // get Shopify cart and perform merge logic as needed
  useEffect(() => {
    // if we don't have an active cart, we need to identify one
    if (cartStoreEnabled && !activeCartId) {
      fetchAndMergeCarts()
    }
  }, [activeCartId, cartStoreEnabled, fetchAndMergeCarts])

  const addItemsToCart = useCallback(
    async (lines: CartLineInput[]) => {
      setStatus('pending')
      try {
        let response: Omit<FigsCart, 'checkoutUrl'> | null = null
        if (activeCartId) {
          response = await checkoutClient.addLines({
            cartId: activeCartId,
            lines: lines.map(item => ({
              ...item,
              // ensure `quantity` is always at least 1
              quantity: item.quantity || 1,
            })),
          })
        } else {
          response = await checkoutClient.createCart({
            lines,
          })
        }
        if (response) {
          setActiveCartId(response.id)
          setShopifyCart(response)
          setShouldAugment(true)
        }
        setStatus('resolved')
      } catch {
        setStatus('rejected')
      }
    },
    [activeCartId, checkoutClient]
  )

  const removeItemsFromCart = useCallback(
    async (lineIdsToRemove: Array<CartLineUpdateInput['id']>) => {
      setStatus('pending')
      try {
        if (activeCartId) {
          const response = await checkoutClient.updateLines({
            cartId: activeCartId,
            lines: lineIdsToRemove.map(lineId => ({
              id: lineId,
              quantity: 0,
            })),
          })
          if (response) setShopifyCart(response)
        }
        setStatus('resolved')
      } catch {
        setStatus('rejected')
      }
    },
    [activeCartId, checkoutClient]
  )

  const updateItemsInCart = useCallback(
    async (lines: CartLineUpdateInput[]) => {
      setStatus('pending')
      try {
        if (activeCartId) {
          const response = await checkoutClient.updateLines({
            cartId: activeCartId,
            lines: lines.map(line => ({
              ...line,
            })),
          })
          if (response) setShopifyCart(response)
        }
        setStatus('resolved')
      } catch {
        setStatus('rejected')
      }
    },
    [activeCartId, checkoutClient]
  )

  const addCartAttributes = useCallback(
    async (attributes: Array<CustomAttributeInput>) => {
      try {
        if (activeCartId) {
          const response = await checkoutClient.updateCartAttributes({
            cartId: activeCartId,
            attributes,
          })
          if (response) setShopifyCart(response)
        }
      } catch {
        // No status to update or error to report as
        // RUM reporting exists in `checkoutClient`.
      }
    },
    [activeCartId, checkoutClient]
  )

  const retrieveCheckoutUrl = useCallback(
    async (queryParams?: Record<string, string>) => {
      try {
        if (activeCartId) {
          const checkoutUrl = await checkoutClient.fetchCheckoutUrl(activeCartId, queryParams)
          return checkoutUrl
        }
      } catch {
        // No status to update or error to report as
        // RUM reporting exists in `checkoutClient`.
      }
      return null
    },
    [activeCartId, checkoutClient]
  )

  const _testing_internals_syncCartStoreAcrossTabs = useCallback(async () => {
    if (cartStoreEnabled && document.visibilityState === 'visible' && activeCartId) {
      const syncedCart = await checkoutClient.fetchCart(activeCartId)
      if (
        syncedCart &&
        JSON.stringify(syncedCart) !== JSON.stringify(shopifyCart) &&
        localStorageCart &&
        // if lengths match, then we can be sure the localStorage tab sync process has completed first
        Object.entries(localStorageCart).length === cart.lines.length
      ) {
        setShopifyCart(syncedCart)
        setShouldAugment(true)
      }
    }
  }, [activeCartId, cartStoreEnabled, checkoutClient, localStorageCart, shopifyCart, cart.lines])

  useEffect(() => {
    document.addEventListener('visibilitychange', _testing_internals_syncCartStoreAcrossTabs)
    return () => {
      document.removeEventListener('visibilitychange', _testing_internals_syncCartStoreAcrossTabs)
    }
  }, [_testing_internals_syncCartStoreAcrossTabs])

  return {
    status,
    cart,
    addItemsToCart,
    removeItemsFromCart,
    updateItemsInCart,
    addCartAttributes,
    retrieveCheckoutUrl,
  }
}
